import type { IScannerControls } from "@zxing/browser";
import { CameraOutlined, PictureOutlined, ScanOutlined } from "@ant-design/icons";
import { Alert, Button, Popover, Space, Typography, Upload } from "antd";
import { useEffect, useRef, useState } from "react";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { normalizeVinInput } from "../../shared/utils/formatters";

type FieldScanMode = "vin" | "plate";

export function FieldScanButton({ mode, onResult }: {
  mode: FieldScanMode;
  onResult: (value: string) => void;
}) {
  const { text } = useAppConfig();
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const stopCamera = () => {
    controlsRef.current?.stop();
    controlsRef.current = undefined;
    setScanning(false);
  };

  useEffect(() => () => controlsRef.current?.stop(), []);

  const acceptValue = (rawValue: string) => {
    const value = parseScannedField(rawValue, mode);
    if (!value) {
      setError(mode === "vin" ? text.invalidVinScan : text.invalidPlateScan);
      return;
    }
    onResult(value);
    stopCamera();
    setError(undefined);
    setOpen(false);
  };

  const startCamera = async () => {
    setError(undefined);
    setScanning(true);
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    if (!videoRef.current) return;
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      controlsRef.current = await reader.decodeFromConstraints(
        { audio: false, video: { facingMode: { ideal: "environment" } } },
        videoRef.current,
        result => {
          if (result) acceptValue(result.getText());
        }
      );
    } catch {
      setScanning(false);
      setError(text.cameraUnavailable);
    }
  };

  const decodePhoto = async (file: File) => {
    setBusy(true);
    setError(undefined);
    const url = URL.createObjectURL(file);
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const result = await new BrowserMultiFormatReader().decodeFromImageUrl(url);
      acceptValue(result.getText());
    } catch {
      setError(text.photoScanFailed);
    } finally {
      URL.revokeObjectURL(url);
      setBusy(false);
    }
    return false;
  };

  const content = <div className="scan-popover">
    <Typography.Title level={5}><ScanOutlined /> {mode === "vin" ? text.scanVin : text.scanPlate}</Typography.Title>
    <Typography.Paragraph type="secondary">{text.fieldScanHint}</Typography.Paragraph>
    {error && <Alert type="warning" showIcon message={error} />}
    {scanning
      ? <div className="scan-camera"><video ref={videoRef} muted playsInline /><div className="scan-frame" /><Button onClick={stopCamera}>{text.stopCamera}</Button></div>
      : <Space direction="vertical" style={{ width: "100%" }}>
        <Button block icon={<CameraOutlined />} onClick={() => void startCamera()}>{text.useCamera}</Button>
        <Upload accept="image/*" showUploadList={false} beforeUpload={decodePhoto}>
          <Button block loading={busy} icon={<PictureOutlined />}>{text.chooseFromPhotos}</Button>
        </Upload>
      </Space>}
  </div>;

  return <Popover
    trigger="click"
    placement="bottomRight"
    open={open}
    onOpenChange={next => {
      setOpen(next);
      setError(undefined);
      if (!next) stopCamera();
    }}
    content={content}
  >
    <Button type="text" size="small" icon={<ScanOutlined />} aria-label={mode === "vin" ? text.scanVin : text.scanPlate} />
  </Popover>;
}

function parseScannedField(rawValue: string, mode: FieldScanMode) {
  const normalized = rawValue.trim().toUpperCase();
  if (mode === "vin") {
    const exact = normalizeVinInput(normalized);
    if (/^[A-HJ-NPR-Z0-9]{17}$/.test(exact)) return exact;
    return normalized.match(/[A-HJ-NPR-Z0-9]{17}/)?.[0] ?? "";
  }
  const plate = normalized.replace(/[^A-Z0-9-]/g, "");
  return plate.length >= 2 && plate.length <= 10 ? plate : "";
}
